import { useSession } from "session";

/**
 * What we pay a service partner, and which one it is, are supply-side facts.
 * The API withholds both from anyone without this permission, so a partner
 * looking at these tables gets empty cells where the rest of us see a
 * provider and a rate. The columns come out entirely rather than standing
 * there blank.
 *
 * Kept in one place, and keyed off the same permission the API gates on, so
 * the screens cannot drift apart from each other or from the server.
 */
export const COST_PERMISSION = "ledger.read_all";

/** Column ids that show something only the supply side should see. */
const COST_COLUMNS = new Set(["serviceProvider", "settlementRate"]);

export function useCanSeeCost(): boolean {
  const { can } = useSession();
  return can(COST_PERMISSION);
}

/**
 * The same columns, minus the ones this viewer may not see.
 *
 * Takes the decision as an argument rather than reading the session itself,
 * because the column arrays are built in render and a hook cannot be called
 * conditionally beside them.
 */
export function visibleColumns<T extends { id?: string; accessorKey?: string }>(
  columns: T[],
  canSeeCost: boolean,
): T[] {
  if (canSeeCost) return columns;
  return columns.filter(
    (column) => !COST_COLUMNS.has(column.id ?? column.accessorKey ?? ""),
  );
}
